import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import AlertPopup from '../../../components/alert-popup';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import Button from '../../../components/button';
import FormSelect from '../../../components/dropdown/formSelect';
import Filter from '../../../components/filter';
import Search from '../../../components/search/search';
import Table from '../../../components/table';
import ToastComponent from '../../../components/toast';
import Heading from '../../../components/typography';
import InitialValuesForm from '../../../constants/initialValues';
import { COLORS } from '../../../constants/style/color';
import { InvitationBlock, ResetFilterWrapper } from '../../../constants/style/common.styled';
import { tradePreClearanceHeader } from '../../../constants/table-headers/tradePreClearanceHeader';
import {
  useBoolean,
  useGetAllDepartments,
  useGetAllEmployeeType,
  useGetAllJobTitles,
  useGetTablePreferences,
  useGetTradePreClearanceList,
  usePostTablePreferences,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import { TradePreClearanceService } from '../../../services/wealthlane-customer-services';
import {
  InvitationBlockData,
  type ModalStatusType,
  type TableParamsType,
} from '../../../types/common';
import { FilterBlock } from '../../participants/participant.styled';
import { SettingSystemUserHeader, SystemUserContainer } from '../system-users/systemUserStyled';
import TradePreClearanceModal from './tradePreClearanceModal';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';

import Spinners from '../../../components/spinner';
import { axiosInstance } from '../../../services/apiInterceptor';

const TradePreClearanceList = (): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const queryClient = useQueryClient();
  // const [tableInfo, setTableInfo] = useState<TableParamsType>(InitialValuesForm.tableParams);
  const { tableInfo, setTableInfo } = useDynamicTable();
  const [headerList, setHeaderList] = useState<string[]>([]);

  useEffect(() => {
    setTableInfo(InitialValuesForm.tableParams);
  }, []);

  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [checkedValues, setCheckedValues] = useState<any>([]);
  const [searchQuery, setSearchQuery] = useState<string>();
  const [open, setOpen] = useState<boolean>(false);
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
  } = useBoolean(false);

  const { data, status } = useGetTradePreClearanceList(handleRequest, tableInfo);
  const { data: departmentOptions, isLoading: isLoadingDepartmentOptions } =
    useGetAllDepartments(handleRequest);
  const { data: employeeTypeOptions, isLoading: isLoadingEmployeeTypeOptions } =
    useGetAllEmployeeType(handleRequest);
  const { data: jobTitleOptions, isLoading: isLoadingJobTitleOptions } =
    useGetAllJobTitles(handleRequest);

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'TRADE_PRE_CLEARANCE_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'Trade pre-clearance');

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const useDeleteTradePreClearance = (): any => {
    return useMutation(
      async () => {
        return await handleRequest(
          TradePreClearanceService.postApiCustomerApiAppTradePreClearanceRemovePreclearance(
            checkedValues
          )
        );
      },
      {
        onSuccess: (response: any) => {
          if (response != null) {
            closeConfirmModal();
            setCheckedValues([]);
            toast.success(
              <ToastComponent label={'Success'} message={'Trade pre clearance deleted.'} />
            );
            void queryClient.invalidateQueries('getTradePreClearanceList');
          }
        },
        onError: (err) => {
          console.log('error', err);
        },
      }
    );
  };
  const { mutate, isLoading } = useDeleteTradePreClearance();

  const tableActions = [
    {
      action: <div className='delete-label'>Remove from list</div>,
      onClick: async (id: string, rowData: any) => {
        setCheckedValues([rowData?.id]);
        setModalStatus('Delete');
        openConfirmModal();
      },
      id: 'removeTradePreClearanceAction',
    },
  ];
  const tableHeaderList = tradePreClearanceHeader({ actions: schemaData?.columns, tableActions })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const tradeclearanceApprovalHeader = (): any[] => {
    return tradePreClearanceHeader({ actions: schemaData?.columns, tableActions }).filter(
      (item) => item.isSubHeader === false || headerList.includes(item.accessor)
    );
  };
  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Trade Clearance Approval',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };

  const tradeHeader = (): any[] => {
    return tradePreClearanceHeader({ actions: schemaData?.columns, tableActions }).filter(
      (item) => item.isSubHeader === false || headerList.includes(item.accessor)
    );
  };

  const schema = React.useMemo(
    () => ({
      columns: tradeHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );

  const renderRightToolbarButton = () => {
    return (
      <Button
        // as='Create'
        variant={'primary'}
        title={'Add Trade Pre-Clearance'}
        icon={'add_circle_outline'}
        id="addTradePreClearanceButton"
        onClick={() => {
          // setUsersInitialData(usersInitialValues);
          setModalStatus('Add');
          // setDataRow('');
          openModal();
        }}
      />
    );
  };
  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  const handleRemoveSelected = () => {
    setModalStatus('Delete');
    openConfirmModal();
  };

  const inivitationBlockDatas: InvitationBlockData = {
    buttons: [
      {
        variant: 'secondary-outline',
        title: 'Remove Selected',
        icon: 'add',
        fontColor: COLORS.Purple,
        borderColor: COLORS.Purple,
        handleAction: handleRemoveSelected,
      },
    ],
    heading: {
      title: 'Trade pre-clearance',
      showHeading: true,
    },
  };
  return (
    <SystemUserContainer data-page="tradePreClearance" id="pageContainer">
      <div className='flex justify-between items-center' id="pageHeader">
        <SettingSystemUserHeader id="headerContent">
          <Heading variant='h3' title='Trade Pre-Clearance' id="pageTitle" />
          <Heading
            variant='body-s'
            title='Secure your account & manage your password.'
            color={`${COLORS.Gray}`}
            id="pageDescription"
          />
        </SettingSystemUserHeader>
      </div>

      <AnimationWrapper>
        {isModalOpen && (
          <TradePreClearanceModal
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            modalStatus={modalStatus}
            setModalStatus={setModalStatus}
            departmentOptions={departmentOptions}
            employeeTypeOptions={employeeTypeOptions}
            jobTitleOptions={jobTitleOptions}
            loading={false}
            id="tradePreClearanceModal"
          />
        )}
      </AnimationWrapper>
      <DynamicTable
        id="tradePreClearanceTable"
        schema={schema}
        data={data?.items ?? []}
        totalCounts={data?.totalCount ?? 10}
        loading={status}
        showCheckBox={true}
        onChecked={(values: string[]) => {
          setCheckedValues(values);
        }}
        checkedValues={checkedValues}
        components={{
          renderRightToolbarButton,
        }}
        inivitationBlockDatas={inivitationBlockDatas}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
      />
      {/* <Table
        columns={tradePreClearanceHeader(actions)}
        data={data?.items ?? []}
        loading={status}
        showCheckBox={true}
        showPagination
        totalCounts={data?.totalCount ?? 10}
        tableInfo={tableInfo}
        setTableInfo={setTableInfo}
        onChecked={(values: string[]) => {
          setCheckedValues(values);
        }}
        checkedValues={checkedValues}
      /> */}
      {isConfirmOpen && (
        <AlertPopup
          id="deleteTradePreClearanceConfirmModal"
          isShow={isConfirmOpen}
          handleSubmit={() => mutate(checkedValues)}
          closeModal={closeConfirmModal}
          modalStatus={modalStatus}
          title={`${checkedValues.length > 1 ? 'these' : 'this'} trade pre-clearance`}
          loading={isLoading}
        />
      )}
    </SystemUserContainer>
  );
};

export default withDynamicTableContext(TradePreClearanceList, false, axiosInstance);
